import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Segmented, Select, Switch, Tooltip, Typography } from "antd";
import { Mafs, Coordinates, Polygon, Line, Text, Theme } from "mafs";

/**
 * ChanceSimulator (feedback visualisation)
 *
 * Flagship behaviours:
 * - One-click presets
 * - Run 10/50/200/1000 with speed control
 * - Theoretical overlay (ghost) vs experimental frequency
 * - Optional cumulative proportion line for one selected outcome
 */
export const ChanceSimulator = ({ config }) => {
  const cfg = config || {};

  // -------------------------
  // Helpers
  // -------------------------
  const safeText = (s, fallback = "") => {
    const t = (s ?? "").toString().trim();
    return t ? t : fallback;
  };

  const clampInt = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

  const normalize = (s) => safeText(s).toLowerCase().replace(/[-\s]+/g, "_");

  // Deterministic PRNG (Mulberry32)
  const mulberry32 = (seed) => {
    let a = seed >>> 0;
    return () => {
      a |= 0;
      a = (a + 0x6d2b79f5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };

  const coerceSeed = (v) => {
    if (typeof v === "number" && Number.isFinite(v)) return (v | 0) >>> 0;
    if (typeof v === "string" && v.trim()) {
      // Simple string hash
      let h = 2166136261;
      for (let i = 0; i < v.length; i++) {
        h ^= v.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return h >>> 0;
    }
    return (Math.random() * 2 ** 32) >>> 0;
  };

  // -------------------------
  // Presets
  // -------------------------
  const PRESETS = useMemo(
    () => ({
      fair_coin: {
        title: "Fair coin",
        outcomes: [
          { label: "H", weight: 1 },
          { label: "T", weight: 1 },
        ],
        focusOutcomeLabel: "H",
      },
      dice: {
        title: "Dice (1–6)",
        outcomes: [
          { label: "1", weight: 1 },
          { label: "2", weight: 1 },
          { label: "3", weight: 1 },
          { label: "4", weight: 1 },
          { label: "5", weight: 1 },
          { label: "6", weight: 1 },
        ],
        focusOutcomeLabel: "6",
      },
      biased_spinner: {
        title: "Biased spinner",
        outcomes: [
          { label: "A", weight: 1 },
          { label: "B", weight: 1 },
          { label: "C", weight: 2 },
          { label: "D", weight: 4 },
        ],
        focusOutcomeLabel: "D",
      },
      bag_of_marbles: {
        title: "Bag of marbles",
        outcomes: [
          { label: "Red", weight: 5 },
          { label: "Blue", weight: 3 },
          { label: "Green", weight: 2 },
        ],
        focusOutcomeLabel: "Red",
      },
    }),
    []
  );

  const defaultRunButtons = Array.isArray(cfg.runButtons) && cfg.runButtons.length > 0 ? cfg.runButtons : [10, 50, 200, 1000];
  const defaultSpeed = normalize(cfg.speedMode || "animated");
  const defaultShowTheory = cfg.showTheoreticalOverlay !== false;
  const defaultShowCum = cfg.showCumulativeProportion !== false;

  const initialPresetKey = (() => {
    const p = normalize(cfg.preset || "");
    if (p && PRESETS[p]) return p;
    // Heuristic: if outcomes look like dice, choose dice
    const labels = (Array.isArray(cfg.outcomes) ? cfg.outcomes : []).map((o) => safeText(o?.label)).filter(Boolean);
    if (labels.length === 2 && labels.includes("H") && labels.includes("T")) return "fair_coin";
    if (labels.length === 6 && labels.every((x) => ["1", "2", "3", "4", "5", "6"].includes(x))) return "dice";
    return "fair_coin";
  })();

  const [presetKey, setPresetKey] = useState(initialPresetKey);
  const [speedMode, setSpeedMode] = useState(defaultSpeed === "fast" ? "fast" : "animated");
  const [showTheory, setShowTheory] = useState(!!defaultShowTheory);
  const [showCum, setShowCum] = useState(!!defaultShowCum);

  // Seed control
  const cfgSeedMode = normalize(cfg.seedMode || "new_seed");
  const [seedLocked, setSeedLocked] = useState(cfgSeedMode === "fixed_seed");
  const [seed, setSeed] = useState(coerceSeed(cfg.seed ?? `${presetKey}-${Date.now()}`));

  // Simulation state
  const [trials, setTrials] = useState(0);
  const [counts, setCounts] = useState({});
  const [running, setRunning] = useState(false);

  // Cumulative history for a selected outcome
  const [focusOutcome, setFocusOutcome] = useState(null);
  const [history, setHistory] = useState([]); // [{t, p}]

  // Timer refs
  const timerRef = useRef(null);
  const rngRef = useRef(null);

  // -------------------------
  // Build active model (from preset OR config)
  // -------------------------
  const active = useMemo(() => {
    const preset = PRESETS[presetKey] || PRESETS.fair_coin;
    const fromConfigOutcomes = Array.isArray(cfg.outcomes) && cfg.outcomes.length >= 2 ? cfg.outcomes : null;

    const rawOutcomes = fromConfigOutcomes || preset.outcomes;

    // Sanitise outcomes
    const out = [];
    const seen = new Set();
    for (const o of rawOutcomes) {
      if (!o) continue;
      const label = safeText(o.label, "");
      if (!label) continue;
      const key = normalize(label);
      if (seen.has(key)) continue;
      seen.add(key);
      const w = Number.isFinite(Number(o.weight)) && Number(o.weight) > 0 ? Number(o.weight) : (Number.isFinite(Number(o.probability)) && Number(o.probability) > 0 ? Number(o.probability) : 1);
      out.push({
        label,
        weight: w,
        colorHex: typeof o.colorHex === "string" ? o.colorHex : null,
      });
      if (out.length >= 12) break;
    }

    const safeOutcomes = out.length >= 2 ? out : preset.outcomes;
    const sumW = safeOutcomes.reduce((a, b) => a + (Number.isFinite(b.weight) ? b.weight : 0), 0) || 1;

    const outcomes = safeOutcomes.map((o, idx) => ({
      ...o,
      weight: o.weight,
      p: o.weight / sumW,
      idx,
    }));

    const title = safeText(cfg.title, preset.title);

    const defaultFocus = safeText(cfg.focusOutcomeLabel, preset.focusOutcomeLabel || outcomes[0]?.label || "");
    const focus = outcomes.find((o) => normalize(o.label) === normalize(defaultFocus))?.label || outcomes[0]?.label;

    return { title, outcomes, focus };
  }, [cfg.title, cfg.outcomes, cfg.focusOutcomeLabel, presetKey, PRESETS]);

  // Keep focus outcome in sync
  useEffect(() => {
    setFocusOutcome(active.focus);
  }, [active.focus]);

  // Reset simulation when preset/config changes
  const reset = (opts = { reseed: false }) => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRunning(false);
    setTrials(0);
    setCounts({});
    setHistory([]);
    if (opts.reseed) {
      setSeed(coerceSeed(`${presetKey}-${Date.now()}`));
    }
  };

  // Build RNG whenever seed changes
  useEffect(() => {
    rngRef.current = mulberry32(seed);
  }, [seed]);

  // Ensure counts have keys
  useEffect(() => {
    const init = {};
    for (const o of active.outcomes) init[o.label] = 0;
    setCounts((prev) => ({ ...init, ...prev }));
  }, [active.outcomes]);

  // -------------------------
  // Sampling
  // -------------------------
  const sampleOne = () => {
    const r = rngRef.current ? rngRef.current() : Math.random();
    let acc = 0;
    for (const o of active.outcomes) {
      acc += o.p;
      if (r <= acc) return o.label;
    }
    return active.outcomes[active.outcomes.length - 1].label;
  };

  const pushHistory = (newTrials, newCounts) => {
    if (!showCum) return;
    const label = focusOutcome;
    if (!label) return;
    const c = newCounts[label] ?? 0;
    const p = newTrials > 0 ? c / newTrials : 0;
    setHistory((h) => {
      const next = [...h, { t: newTrials, p }];
      // cap to avoid memory growth
      if (next.length > 500) return next.slice(next.length - 500);
      return next;
    });
  };

  const runBatch = (n) => {
    const batch = clampInt(parseInt(n, 10) || 0, 1, 5000);

    if (speedMode === "fast") {
      const newCounts = { ...counts };
      let newTrials = trials;
      for (let i = 0; i < batch; i++) {
        const lab = sampleOne();
        newCounts[lab] = (newCounts[lab] ?? 0) + 1;
        newTrials += 1;
      }
      setCounts(newCounts);
      setTrials(newTrials);
      pushHistory(newTrials, newCounts);
      return;
    }

    // Animated: chunked updates for smoothness
    if (running) return;
    setRunning(true);

    let remaining = batch;
    let localCounts = { ...counts };
    let localTrials = trials;

    timerRef.current = window.setInterval(() => {
      const step = Math.min(remaining, 12); // ~12 samples per tick
      for (let i = 0; i < step; i++) {
        const lab = sampleOne();
        localCounts[lab] = (localCounts[lab] ?? 0) + 1;
        localTrials += 1;
      }
      remaining -= step;

      setCounts({ ...localCounts });
      setTrials(localTrials);
      pushHistory(localTrials, localCounts);

      if (remaining <= 0) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
        setRunning(false);
      }
    }, 30);
  };

  // Seed behaviour: if not locked, clicking run uses a fresh seed each time (teacher-demo friendly)
  const beforeRun = () => {
    if (!seedLocked) {
      // refresh seed, rebuild RNG
      const newSeed = coerceSeed(`${presetKey}-${Date.now()}`);
      setSeed(newSeed);
    }
  };

  // -------------------------
  // Derived metrics
  // -------------------------
  const empirical = useMemo(() => {
    const e = {};
    for (const o of active.outcomes) {
      const c = counts[o.label] ?? 0;
      e[o.label] = trials > 0 ? c / trials : 0;
    }
    return e;
  }, [active.outcomes, counts, trials]);

  const maxProb = useMemo(() => {
    const tMax = Math.max(...active.outcomes.map((o) => o.p));
    const eMax = Math.max(...active.outcomes.map((o) => empirical[o.label] || 0));
    return Math.max(0.2, tMax, eMax);
  }, [active.outcomes, empirical]);

  const convergence = useMemo(() => {
    if (trials <= 0) return { diff: null, ok: false };
    let maxDiff = 0;
    for (const o of active.outcomes) {
      maxDiff = Math.max(maxDiff, Math.abs((empirical[o.label] || 0) - o.p));
    }
    // heuristic thresholds
    const ok = trials >= 100 && maxDiff <= 0.05;
    return { diff: maxDiff, ok };
  }, [active.outcomes, empirical, trials]);

  const misconceptionText = useMemo(() => {
    if (trials < 30) {
      return "Short runs can look surprising — random variation is normal.";
    }
    if (trials < 150) {
      return "As you run more trials, experimental frequencies start to settle closer to the theoretical probability.";
    }
    return "Long runs stabilise: experimental frequency converges toward the theoretical probability (law of large numbers).";
  }, [trials]);

  // -------------------------
  // Mafs chart primitives
  // -------------------------
  const viewBox = useMemo(() => {
    const n = active.outcomes.length;
    const yMax = Math.min(1.0, maxProb * 1.25);
    return { x: [-0.5, n - 0.5], y: [0, yMax] };
  }, [active.outcomes.length, maxProb]);

  const barPolys = useMemo(() => {
    const n = active.outcomes.length;
    const polys = [];

    const palette = [Theme.blue, Theme.indigo, Theme.green, Theme.orange, Theme.red, Theme.pink, Theme.violet, Theme.cyan];

    for (let i = 0; i < n; i++) {
      const o = active.outcomes[i];
      const x0 = i - 0.32;
      const x1 = i + 0.32;
      const hEmp = empirical[o.label] || 0;
      const hTh = o.p;

      const color = o.colorHex || palette[i % palette.length];

      // Theoretical overlay (ghost)
      polys.push({
        key: `th-${o.label}`,
        points: [
          [x0, 0],
          [x0, hTh],
          [x1, hTh],
          [x1, 0],
        ],
        color,
        opacity: 0.18,
      });

      // Experimental bar
      polys.push({
        key: `emp-${o.label}`,
        points: [
          [x0, 0],
          [x0, hEmp],
          [x1, hEmp],
          [x1, 0],
        ],
        color,
        opacity: 0.85,
      });
    }

    return polys;
  }, [active.outcomes, empirical]);

  const cumLineSegments = useMemo(() => {
    if (!showCum || !history || history.length < 2) return [];

    // Normalize x to [0, 1] range in a mini-chart; we will render in a separate Mafs below
    const segments = [];
    for (let i = 1; i < history.length; i++) {
      segments.push({
        p1: [history[i - 1].t, history[i - 1].p],
        p2: [history[i].t, history[i].p],
      });
    }
    return segments;
  }, [history, showCum]);

  const cumViewBox = useMemo(() => {
    if (!showCum || history.length === 0) return { x: [0, 10], y: [0, 1] };
    const tMax = history[history.length - 1].t;
    return { x: [0, Math.max(10, tMax)], y: [0, 1] };
  }, [history, showCum]);

  // -------------------------
  // Render
  // -------------------------
  const presetOptions = [
    { label: "Fair coin", value: "fair_coin" },
    { label: "Dice", value: "dice" },
    { label: "Biased spinner", value: "biased_spinner" },
    { label: "Bag of marbles", value: "bag_of_marbles" },
  ];

  return (
    <Card className="ch-card chance-sim" bodyStyle={{ padding: 16 }}>
      <div className="flexColumn gap-medium">
        <div className="chance-sim-header flexRow justifyBetween alignCenter">
          <div className="flexColumn">
            <Typography.Title level={4} style={{ margin: 0 }}>
              Chance Simulator
            </Typography.Title>
            <Typography.Text type="secondary">{active.title}</Typography.Text>
          </div>
          <div className="flexRow gap-small alignCenter">
            <Tooltip title="Clear counts and restart">
              <Button onClick={() => reset({ reseed: false })}>Reset</Button>
            </Tooltip>
            <Tooltip title="Reset and generate a new random seed">
              <Button onClick={() => reset({ reseed: true })}>New seed</Button>
            </Tooltip>
          </div>
        </div>

        <div className="chance-sim-controls flexColumn gap-small">
          <div className="flexRow gap-small alignCenter wrap">
            <Typography.Text strong>Preset:</Typography.Text>
            <Select
              value={presetKey}
              onChange={(v) => {
                setPresetKey(v);
                reset({ reseed: true });
              }}
              options={presetOptions}
              style={{ minWidth: 180 }}
            />

            <Typography.Text strong>Speed:</Typography.Text>
            <Segmented
              value={speedMode}
              onChange={(v) => setSpeedMode(v)}
              options={[
                { label: "Animated", value: "animated" },
                { label: "Fast", value: "fast" },
              ]}
            />

            <Typography.Text strong>Replay seed:</Typography.Text>
            <Switch checked={seedLocked} onChange={(v) => setSeedLocked(v)} />

            <Typography.Text strong>Theory overlay:</Typography.Text>
            <Switch checked={showTheory} onChange={(v) => setShowTheory(v)} />

            <Typography.Text strong>Cumulative line:</Typography.Text>
            <Switch checked={showCum} onChange={(v) => setShowCum(v)} />
          </div>

          <div className="flexRow gap-small alignCenter wrap">
            <Typography.Text strong>Run:</Typography.Text>
            {defaultRunButtons.map((n) => (
              <Button
                key={`run-${n}`}
                type="primary"
                disabled={running}
                onClick={() => {
                  beforeRun();
                  runBatch(n);
                }}
              >
                {`Run ${n}`}
              </Button>
            ))}

            <div className="chance-sim-trialbadge">
              <Typography.Text>
                Trials: <strong>{trials}</strong>
              </Typography.Text>
              {convergence.diff != null && (
                <Typography.Text type={convergence.ok ? "success" : "secondary"}>
                  {convergence.ok
                    ? "Converging"
                    : `Max diff ≈ ${(convergence.diff * 100).toFixed(1)}%`}
                </Typography.Text>
              )}
            </div>
          </div>
        </div>

        <Alert
          type="info"
          showIcon
          message="Misconception check"
          description={misconceptionText}
        />

        <div className="chance-sim-chart">
          <div className="chance-sim-chartHeader flexRow justifyBetween alignCenter">
            <Typography.Text strong>Experimental vs theoretical probability</Typography.Text>
            <div className="flexRow gap-small alignCenter">
              <Typography.Text type="secondary">Focus outcome:</Typography.Text>
              <Select
                value={focusOutcome}
                onChange={(v) => {
                  setFocusOutcome(v);
                  setHistory([]);
                }}
                options={active.outcomes.map((o) => ({ label: o.label, value: o.label }))}
                style={{ minWidth: 120 }}
              />
            </div>
          </div>

          <div className="chance-sim-mafs">
            <Mafs viewBox={viewBox} pan={false} zoom={false}>
              <Coordinates.Cartesian xAxis={{ labels: false }} yAxis={{ labels: true }} />

              {/* Bars */}
              {barPolys.map((b) => {
                if (!showTheory && b.key.startsWith("th-")) return null;
                return (
                  <Polygon
                    key={b.key}
                    points={b.points}
                    color={b.color}
                    opacity={b.opacity}
                  />
                );
              })}

              {/* Outcome labels */}
              {active.outcomes.map((o) => (
                <Text
                  key={`lbl-${o.label}`}
                  x={o.idx}
                  y={-0.04}
                  size={14}
                  color={Theme.foreground}
                >
                  {o.label}
                </Text>
              ))}
            </Mafs>
          </div>

          <div className="chance-sim-table">
            <div className="chance-sim-tableHeader">
              <div>Outcome</div>
              <div>Theory</div>
              <div>Experimental</div>
              <div>Count</div>
            </div>
            {active.outcomes.map((o) => {
              const c = counts[o.label] ?? 0;
              const e = empirical[o.label] ?? 0;
              const isFocus = normalize(o.label) === normalize(focusOutcome);
              return (
                <div key={`row-${o.label}`} className={`chance-sim-row ${isFocus ? "is-focus" : ""}`}>
                  <div className="chance-sim-cell strong">{o.label}</div>
                  <div className="chance-sim-cell">{(o.p * 100).toFixed(1)}%</div>
                  <div className="chance-sim-cell">{(e * 100).toFixed(1)}%</div>
                  <div className="chance-sim-cell">{c}</div>
                </div>
              );
            })}
          </div>
        </div>

        {showCum && (
          <div className="chance-sim-cum">
            <Typography.Text strong>{`Cumulative proportion of “${focusOutcome || ""}”`}</Typography.Text>
            <div className="chance-sim-mafsSmall">
              <Mafs viewBox={cumViewBox} pan={false} zoom={false}>
                <Coordinates.Cartesian />
                {cumLineSegments.map((seg, i) => (
                  <Line.Segment
                    key={`seg-${i}`}
                    point1={seg.p1}
                    point2={seg.p2}
                    color={Theme.indigo}
                    weight={2}
                    opacity={0.9}
                  />
                ))}

                {/* Theoretical horizontal line */}
                {showTheory && focusOutcome && (
                  (() => {
                    const th = active.outcomes.find((o) => normalize(o.label) === normalize(focusOutcome))?.p;
                    if (th == null) return null;
                    const xMin = cumViewBox.x[0];
                    const xMax = cumViewBox.x[1];
                    return (
                      <Line.Segment
                        point1={[xMin, th]}
                        point2={[xMax, th]}
                        color={Theme.gray}
                        weight={2}
                        opacity={0.5}
                      />
                    );
                  })()
                )}
              </Mafs>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
